import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import * as XLSX from "xlsx";
import { KTSVG } from "../../../_metronic/helpers";
import { JobReportChart } from "./JobReportChart";
import loadingimage from "../../../Assets/loading-buffering1.gif";
import { StylesConfig } from "react-select";
import moment from "moment";

const JobReport = () => {
  const [journeyData, setJourneyData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJourney, setSelectedJourney] =
    useState<SingleValue<{ value: string; label: string }>>(null);
  const [history, setHistory] = useState<any>({});
  const [bookings, setBookings] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [grossProfit, setGrossProfit] = useState("");
  const [totalookingData, setTotalBookingdata] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [selectedTable, setSelectedTable] = useState<"Bookings" | "Expenses">(
    "Bookings"
  );
  const [progress, setProgress] = React.useState<number>(0);
  const [showProgressBar, setShowProgressBar] = React.useState<boolean>(false);

  const getBadgeClass = (status: string) => {
    switch (status?.trim().toLowerCase()) {
      case "shipment started":
      case "shipment booked":
        return "badge badge-light-info";

      case "collected to shipment":
      case "delivered shipment":
      case "delivered":
        return "badge badge-light-success";

      case "delivered to hub":
      case "shipment progress":
      case "partially delivered": // Fixed issue with extra tab character
        return "badge badge-light-warning";

      case "shipment ended":
      case "pending":
        return "badge badge-light-danger";

      case "main booking":
        return "badge badge-light-primary";

      default:
        return "badge badge-light-secondary";
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://backendcway.causewaycargo.com/superadmin/getjpurneyid",
        {
          method: "GET",
          headers: {
            auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do", // Replace with your auth token
          },
        }
      );
      const result = await response.json();
      if (result?.data) setJourneyData(result.data);
    } catch (err) {
      console.error("Error fetching journey data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const sendJobReport = async (journeyId: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://backendcway.causewaycargo.com/superadmin/jobreport",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
          },
          body: JSON.stringify({ shipment_id: journeyId }),
        }
      );
      const result = await response.json();
      if (result?.data) {
        setHistory(result.data);
        const {
          bookings = [],
          expenses = [],
          gross_profit,
          booking_total_amount,
        } = result.data;
        setBookings(bookings);
        setExpenses(expenses);
        setGrossProfit(gross_profit || "0.00");
        setTotalBookingdata(booking_total_amount || "0.00");
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error fetching job report:", err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSelectChange = (
    option: SingleValue<{ value: string; label: string }>
  ) => {
    if (option) {
      sendJobReport(option.value);
      setSelectedJourney(option);
    }
  };

  const calculateTotals = () => {
    let totalAmount = 0;
    let grossProfitTotal = grossProfit || 0; // Use API value, don't modify grossProfitTotal

    if (selectedTable === "Bookings" && Array.isArray(bookings)) {
      bookings.forEach((booking) => {
        totalAmount += booking?.booking_total_amount
          ? parseFloat(booking.booking_total_amount)
          : 0;
      });
    } else if (selectedTable === "Expenses" && Array.isArray(expenses)) {
      expenses.forEach((expense) => {
        totalAmount += expense?.amount ? parseFloat(expense.amount) : 0;
      });
    }

    return { totalAmount, grossProfitTotal };
  };

  const handleExportToExcel = () => {
    if (!bookings.length && !expenses.length) {
      alert("No data available to export.");
      return;
    }
    setShowProgressBar(true); // 🟢 Show ProgressBar
    setProgress(10); // 🟢 Start at 10%

    const wb = XLSX.utils.book_new(); // Create a new workbook
    const wsData = []; // Array to store all data

    // Function to create spacing between tables
    const addGap = (count = 2) => {
      for (let i = 0; i < count; i++) {
        wsData.push([]); // Push empty row for spacing
      }
    };

    // 🟢 Section 1: Bookings (Starts from Column A)
    wsData.push(["Bookings"]); // Title row
    wsData.push([
      "Booking ID",
      "Paid Amount",
      "Discount",
      "Shipment Status",
      "Payment Status",
      "Need To Pay",
      "Total Amount",
    ]);

    bookings.forEach((item, index) => {
      wsData.push([
        item?.booking_id || item?.split_id || "N/A",
        item?.total_amount || "0.00", // Paid Amount
        item?.total_discount_given || "0.00", // Discount
        item?.shipment_status === "Shipment Started"
          ? "In Transit"
          : item?.shipment_status || "N/A",
        item?.payment_process_status || "N/A", // Payment Status
        item?.total_need_topay || "0.00", // Need To Pay
        item?.booking_value || "0.00", // Total Amount
      ]);
      if (index % 10 === 0) {
        setProgress((prev) => Math.min(prev + 5, 70)); // 🟢 Progress update during heavy loops
      }
    });

    addGap();
    setProgress(75); // Add spacing after this table

    // 🟢 Section 2: Expenses (Starts from Column A)
    wsData.push(["Expenses"]); // Title row
    wsData.push(["Date", "Name", "Comment", "Amount"]);

    expenses.forEach((item, index) => {
      wsData.push([
        moment(item?.created_at).format("DD-MM-YYYY") || "N/A",
        item?.expense_name || "N/A", // Name
        item?.comment || "N/A", // Comment
        item?.amount || "0.00", // Amount
      ]);
      if (index % 10 === 0) {
        setProgress((prev) => Math.min(prev + 2, 85)); // 🟢 Progress update
      }
    });

    addGap(); // Add spacing after this table
    setProgress(90);
    // 🟢 Section 3: Summary (Starts from Column A)
    wsData.push(["Summary"]); // Title row
    wsData.push(["Metric", "Value"]); // Headers

    wsData.push([
      "Booking Total Amount",
      history?.booking_total_amount || "0.00",
    ]);
    wsData.push([
      "Total Discount Amount",
      history?.total_discount_amount || "0.00",
    ]);
    wsData.push([
      "Expenses Total Amount",
      history?.expenses_total_amount || "0.00",
    ]);
    wsData.push(["Total Need to Pay", history?.total_needtopay || "0.00"]);
    wsData.push(["Total Paid Amount", history?.total_paid_amount || "0.00"]);
    wsData.push(["Gross Profit", history?.gross_profit || "0.00"]);
    setProgress(95);
    // 🔹 Convert to worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 🔹 Set Column Widths for Better Spacing
    ws["!cols"] = [
      { wch: 25 }, // Column A (Auto-adjust width)
      { wch: 20 }, // Column B
      { wch: 20 }, // Column C
      { wch: 20 }, // Column D
      { wch: 20 }, // Column E
      { wch: 20 }, // Column F
      { wch: 20 }, // Column G
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Job Report");

    // 🔹 Generate the file name
    const filename = `JobReport_${selectedJourney?.label || "Report"}.xlsx`;
    XLSX.writeFile(wb, filename);
    setProgress(100); // 🟢 100% done
    setTimeout(() => {
      setShowProgressBar(false); // 🟢 Hide after short delay
      setProgress(0);
    }, 1000);
  };
  const journeyOptions = journeyData.map((item) => ({
    value: item.journey_id,
    label: `${item.journey_id} (${item.container_id || "N/A"})`,
  }));

  const paginate = (items: any[], page: number, itemsPerPage: number) =>
    items.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const renderPagination = (totalItems: number, currentPage: number) => {
    if (totalItems <= itemsPerPage) return null;

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const pagination = [];

    for (let page = 1; page <= totalPages; page++) {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        pagination.push(
          <button
            key={page}
            className={`btn btn-light-primary mx-1 px-4 py-2 ${
              currentPage === page ? "active" : ""
            }`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        );
      }

      if (page === currentPage - 2 || page === currentPage + 2) {
        pagination.push(
          <span key={page} className="mx-2 text-muted">
            ...
          </span>
        );
      }
    }

    return (
      <div className="d-flex justify-content-center mt-4 mb-3">
        <button
          className="btn btn-light-primary me-1 px-4 py-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          <KTSVG
            path="/media/icons/duotune/arrows/arr079.svg"
            className="svg-icon-3"
          />
        </button>
        <div>{pagination}</div>
        <button
          className="btn btn-light-primary px-4 py-2 ms-1"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          <KTSVG
            path="/media/icons/duotune/arrows/arr080.svg"
            className="svg-icon-3"
          />
        </button>
      </div>
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const { totalAmount, grossProfitTotal } = calculateTotals();

  const dataToDisplay = selectedTable === "Bookings" ? bookings : expenses;
  const dataToPaginate = paginate(dataToDisplay, currentPage, itemsPerPage);

  const customStyles: StylesConfig = {
    container: (provided) => ({ ...provided, width: "280px" }),
    menu: (provided) => ({ ...provided, width: "280px" }),
  };

  interface OptionType {
    value: string;
    label: string;
  }

  const handleChange = (option: OptionType | null) => {
    if (option) {
      setSelectedTable(option.value as "Bookings" | "Expenses");
    }
  };

  return (
    <div className="card px-lg-3">
      {showProgressBar && (
        <>
          <div className="modal-backdrop shadow-lg rounded-5">
            <div className="modal d-block" tabIndex={-1} role="dialog">
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content pb-10 pt-4">
                  <div className="modal-header py-2 d-flex justify-content-end">
                    <div className="modal-body">
                      <h5 className="mb-4">Exporting Data...</h5>
                      <div className="progress" style={{ height: "20px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                          role="progressbar"
                          style={{ width: `${progress}%` }}
                          aria-valuenow={progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {Math.round(progress)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="card-header gap-4 px-4 align-items-end py-4">
        <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap">
          <div>
            <h3>Job Report</h3>
            <Select
              options={journeyOptions}
              isLoading={isLoading}
              placeholder="Select a journey"
              onChange={handleSelectChange}
              styles={{
                container: (provided: any) => ({ ...provided, width: "280px" }),
                menu: (provided: any) => ({ ...provided, width: "280px" }),
              }}
            />
          </div>

          {(bookings.length !== 0 || expenses.length !== 0) && (
            <div className="d-flex align-items-center mt-8">
              {selectedTable === "Bookings" && bookings.length !== 0 && (
                <div className="px-4 bg-warning py-3 d-flex align-items-center justify-content-center btn">
                  <h4 className="mb-0">
                    Total Amount from Bookings:{" "}
                    {totalookingData.toLocaleString()} QAR
                  </h4>
                </div>
              )}

              {selectedTable === "Expenses" && expenses.length !== 0 && (
                <div className="px-4 bg-warning py-3 d-flex align-items-center justify-content-center btn">
                  <h4 className="mb-0">
                    Total Amount from Expenses:{" "}
                    {calculateTotals().totalAmount.toLocaleString()} QAR
                  </h4>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap">
          {bookings.length !== 0 && expenses.length !== 0 && (
            <div className="d-flex gap-4 align-items-center flex-wrap flex-md-nowrap">
              <Select
                options={[
                  { value: "Bookings", label: "Bookings" },
                  { value: "Expenses", label: "Expenses" },
                ]}
                value={{ value: selectedTable, label: selectedTable }}
                onChange={handleChange}
              />
              <div
                className={`px-4 py-3 d-flex btn ${
                  parseFloat(grossProfit) < 0
                    ? "bg-danger text-white"
                    : "bg-success text-white"
                }`}
              >
                <span>
                  Total Gross Profit: {parseFloat(grossProfit).toLocaleString()}{" "}
                  QAR
                </span>
              </div>
            </div>
          )}
          <button
            className="btn btn-danger"
            onClick={handleExportToExcel}
            disabled={!bookings.length && !expenses.length}
          >
            Export to Excel
          </button>
        </div>
      </div>
      <div className="card-body">
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <img src={loadingimage} alt="Loading..." className="w-20" />
          </div>
        ) : (
          <>
            {dataToDisplay.length === 0 ? (
              <p className="text-center  py-4 text-gray-500">
                🚫 No reports available. Please select a journey to view the
                report.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bolder fw-bold fs-5">
                      {selectedTable === "Bookings" ? (
                        <>
                          <th className="min-w-120px">Booking ID</th>
                          <th className="min-w-120px">Paid Amount</th>
                          <th className="min-w-120px">Discount</th>
                          <th className="min-w-120px">Shipment Status</th>
                          <th className="min-w-120px">Payment Status</th>
                          <th className="min-w-120px">Need To Pay</th>
                          <th className="min-w-120px">Total Amount</th>
                        </>
                      ) : (
                        <>
                          <th className="min-w-120px">Date</th>
                          <th className="min-w-120px">Name</th>
                          <th className="min-w-120px">Comment</th>
                          <th className="min-w-120px">Amount</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {dataToPaginate.map((item, index) => (
                      <tr key={index}>
                        {Array.isArray(bookings) &&
                        selectedTable === "Bookings" ? (
                          <>
                            <td className=" fw-bold fs-6 text-dark fw-bolder text-hover-primary">
                              {item?.booking_id
                                ? item?.booking_id
                                : item?.split_id || "N/A"}
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.total_amount ? item.total_amount : "0.00"}{" "}
                              QAR
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.total_discount_given
                                ? item.total_discount_given
                                : "0.00"}{" "}
                              QAR
                            </td>

                            <td className="text-gray-600 fw-bold fs-6">
                              <span
                                className={`badge ${getBadgeClass(
                                  item?.shipment_status || "N/A"
                                )}`}
                              >
                                {item?.shipment_status === "Shipment Started"
                                  ? "In Transit"
                                  : item?.shipment_status}
                              </span>
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.payment_process_status}
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.total_need_topay
                                ? item?.total_need_topay
                                : "0.00"}{" "}
                              QAR
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.booking_value
                                ? item.booking_value
                                : "0.00"}{" "}
                              QAR
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.created_at
                                ? moment(item?.created_at).format("DD-MM-YYYY")
                                : "N/A"}
                            </td>

                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.expense_name || "N/A"}
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.comment || "N/A"}
                            </td>
                            <td className="text-gray-600 fw-bold fs-6">
                              {item?.amount ? item.amount : "0.00"} QAR
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {renderPagination(dataToDisplay.length, currentPage)}
              </div>
            )}
          </>
        )}
      </div>
      {/* <JobReportChart totalAmount={totalAmount} grossProfitTotal={grossProfitTotal} /> */}
    </div>
  );
};

export default JobReport;
